import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Spinner } from '@patternfly/react-core';
import { DownloadIcon, ExternalLinkAltIcon } from '@patternfly/react-icons';
import { InvoiceResponse } from '@app/Subscription/types/subscription';
import { getInvoices } from '@app/utils/utils';

const styles: Record<string, React.CSSProperties> = {
    sectionCard: {
        background: '#ffffff',
        borderRadius: '14px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    },
    sectionTitle: { fontSize: '16px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } as React.CSSProperties,
    th: {
        textAlign: 'left' as const,
        padding: '8px 12px',
        background: '#f8fafc',
        color: '#475569',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em'
    },
    td: { padding: '12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    statusPill: {
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'capitalize' as const,
        display: 'inline-block'
    },
    empty: { textAlign: 'center' as const, padding: '24px', color: '#6b7280', fontSize: '14px' },
    error: { color: '#b91c1c', fontSize: '13px', marginTop: '8px' }
};

const statusStyle = (status: string): React.CSSProperties => {
    switch (status) {
        case 'paid':
            return { ...styles.statusPill, background: '#dcfce7', color: '#166534' };
        case 'open':
            return { ...styles.statusPill, background: '#fef3c7', color: '#92400e' };
        case 'void':
        case 'uncollectible':
            return { ...styles.statusPill, background: '#fee2e2', color: '#991b1b' };
        default:
            return { ...styles.statusPill, background: '#f3f4f6', color: '#4b5563' };
    }
};

function formatAmount(cents: number, currency: string): string {
    const symbol = currency.toLowerCase() === 'usd' ? '$' : currency.toUpperCase() + ' ';
    return symbol + (cents / 100).toFixed(2);
}

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return iso;
    }
}

interface InvoicesSectionProps {
    /** Render nothing (instead of "No invoices yet.") when the list is empty — used for
     * plan-less users, who still get one-off credit top-up invoices but shouldn't see an
     * empty billing table before their first purchase. */
    hideWhenEmpty?: boolean;
}

export const InvoicesSection: React.FC<InvoicesSectionProps> = ({ hideWhenEmpty = false }) => {
    const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        getInvoices()
            .then((list) => {
                if (!cancelled) {
                    setInvoices(list);
                    setError(null);
                }
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || 'Failed to load invoices');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (hideWhenEmpty && (loading || (invoices.length === 0 && !error))) {
        return null;
    }

    return (
        <div style={styles.sectionCard}>
            <div style={styles.sectionTitle}>Invoices</div>

            {loading ? (
                <div style={styles.empty}>
                    <Spinner size="md" />
                </div>
            ) : invoices.length === 0 ? (
                <div style={styles.empty}>No invoices yet.</div>
            ) : (
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Date</th>
                            <th style={styles.th}>Number</th>
                            <th style={styles.th}>Description</th>
                            <th style={styles.th}>Amount</th>
                            <th style={styles.th}>Status</th>
                            <th style={{ ...styles.th, textAlign: 'right' }}>Receipt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map((inv) => (
                            <tr key={inv.id}>
                                <td style={styles.td}>{formatDate(inv.created)}</td>
                                <td style={styles.td}>{inv.number || inv.id.slice(0, 14)}</td>
                                <td style={styles.td}>{inv.description || 'Subscription'}</td>
                                <td style={styles.td}>
                                    {formatAmount(inv.amountPaid, inv.currency)}
                                </td>
                                <td style={styles.td}>
                                    <span style={statusStyle(inv.status)}>{inv.status}</span>
                                </td>
                                <td style={{ ...styles.td, textAlign: 'right' }}>
                                    {inv.hostedInvoiceUrl && (
                                        <Button
                                            component="a"
                                            href={inv.hostedInvoiceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="link"
                                            icon={<ExternalLinkAltIcon />}
                                            iconPosition="right"
                                            isInline
                                        >
                                            View
                                        </Button>
                                    )}
                                    {inv.invoicePdf && (
                                        <Button
                                            component="a"
                                            href={inv.invoicePdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="link"
                                            icon={<DownloadIcon />}
                                            iconPosition="right"
                                            isInline
                                            style={{ marginLeft: '8px' }}
                                        >
                                            PDF
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {error && <div style={styles.error}>{error}</div>}
        </div>
    );
};
